import { publicClient, authClient, tokenStore } from './baseAPI';
import { config } from '@/config';
import type { User } from '@/types/user';

export interface LoginRequest {
  email: string;
  password: string;
}

// Mirrors prowatchly_api UserRegistrationRequest exactly. The backend
// enforces: password >= 8 chars with at least one upper/lower/digit,
// terms_accepted must be true, work_email validated as EmailStr,
// timezone validated as IANA name via Python's stdlib zoneinfo.
export interface RegisterRequest {
  first_name: string;
  last_name: string;
  work_email: string;
  company: string;
  industry_type: IndustryType;
  // IANA timezone like "America/New_York" or "Asia/Kolkata". Auto-detected
  // from the browser at signup via Intl.DateTimeFormat. Omitted = backend
  // defaults to "UTC".
  timezone?: string;
  password: string;
  terms_accepted: boolean;
}

export type IndustryType =
  | 'retail'
  | 'manufacturing'
  | 'healthcare'
  | 'education'
  | 'hospitality'
  | 'transportation'
  | 'warehouse'
  | 'office'
  | 'residential'
  | 'other';

export interface TokenBundle {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

export interface AuthResponse {
  user: User;
  tokens: TokenBundle;
  message: string;
}

export const authAPI = {
  login: async (payload: LoginRequest): Promise<AuthResponse> => {
    const { data } = await publicClient.post<AuthResponse>(config.api.auth.login, payload);
    tokenStore.setAccess(data.tokens.access_token);
    tokenStore.setRefresh(data.tokens.refresh_token);
    return data;
  },

  // Returns the same AuthResponse shape as /login so the caller can drop
  // straight into an authenticated session without a separate sign-in step.
  // Tokens are persisted via tokenStore so the rest of the app's authClient
  // is immediately usable.
  register: async (payload: RegisterRequest): Promise<AuthResponse> => {
    const { data } = await publicClient.post<AuthResponse>(
      config.api.auth.register,
      payload,
    );
    tokenStore.setAccess(data.tokens.access_token);
    tokenStore.setRefresh(data.tokens.refresh_token);
    return data;
  },

  me: async (): Promise<User> => {
    const { data } = await authClient.get<User>(config.api.auth.me);
    return data;
  },

  // Logout is bulletproof: tokens are ALWAYS cleared and the function NEVER
  // throws. The server-side call is fire-and-forget — a hung backend, an
  // expired token, or a network blip must not block the user from signing
  // out. Server-side blacklist is nice-to-have, not load-bearing; local
  // state (tokens + user) is what actually controls access.
  logout: async (): Promise<void> => {
    try {
      await authClient.post(config.api.auth.logout);
    } catch {
      /* swallow — local cleanup runs regardless */
    } finally {
      tokenStore.clear();
    }
  },

  // Email verification — called by the /verify-email page after a user
  // clicks the link from their welcome email. Backend route returns
  // `MessageResponse` (message + success bool); on the wire that's also
  // what we get for 4xx (FastAPI puts the validation message in
  // `detail`). We use publicClient because the user is NOT signed in
  // when they hit the verification link (they just registered, but
  // tokens aren't carried via email links).
  verifyEmail: async (token: string): Promise<{ message: string; success: boolean }> => {
    const { data } = await publicClient.get<{ message: string; success: boolean }>(
      config.api.auth.verifyEmail,
      { params: { token } },
    );
    return data;
  },

  // ── Password flows ─────────────────────────────────────────────────────
  //
  // `requestPasswordReset` always resolves 200 with a generic message —
  // the backend deliberately does NOT reveal whether the email exists
  // (tenant-enumeration defence). The /forgot-password page renders the
  // same "if the email exists, we sent a link" success state regardless.

  requestPasswordReset: async (email: string): Promise<{ message: string; success: boolean }> => {
    const { data } = await publicClient.post<{ message: string; success: boolean }>(
      config.api.auth.requestPasswordReset,
      { email },
    );
    return data;
  },

  // Consumes the token from the emailed `/reset-password?token=…` link.
  // Backend field is `password` (inherited from PasswordMixin); 400 means
  // invalid/expired token OR password failed strength validation.
  confirmPasswordReset: async (
    token: string,
    password: string,
  ): Promise<{ message: string; success: boolean }> => {
    const { data } = await publicClient.post<{ message: string; success: boolean }>(
      config.api.auth.confirmPasswordReset,
      { token, password },
    );
    return data;
  },

  // For authenticated users. Backend verifies `old_password` against the
  // stored hash before updating; 400 on mismatch. `password` is the new one
  // (PasswordMixin contract); `old_password` is the current one.
  changePassword: async (
    oldPassword: string,
    newPassword: string,
  ): Promise<{ message: string; success: boolean }> => {
    const { data } = await authClient.post<{ message: string; success: boolean }>(
      config.api.auth.changePassword,
      { old_password: oldPassword, password: newPassword },
    );
    return data;
  },
};
